import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createApi, getInviteInfo } from '../services/api.js';

interface InviteInfo {
  team_name: string;
  tier: string;
  member_count: number;
}

export function JoinTeamPage({ token }: { token: string | null }) {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    getInviteInfo(inviteToken).then((res) => {
      if (res.ok) {
        setInfo(res.data);
      } else {
        setError(res.error.message);
      }
      setLoading(false);
    });
  }, [inviteToken]);

  const handleJoin = useCallback(async () => {
    if (!token || !inviteToken) return;
    setJoining(true);
    setError(null);
    const api = createApi(token);
    const res = await api.joinTeam(inviteToken);
    if (res.ok) {
      setSuccess(true);
      setTimeout(() => navigate('/pricing'), 2000);
    } else {
      setError(res.error.message);
    }
    setJoining(false);
  }, [token, inviteToken, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-50">
        <p className="text-ink-muted text-sm">Loading invite...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-surface-200 bg-white p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
          <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {error && !info ? (
          <>
            <h1 className="text-lg font-bold text-ink">Invalid Invite</h1>
            <p className="mt-2 text-sm text-ink-muted">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark transition"
            >
              Go to Login
            </button>
          </>
        ) : success ? (
          <>
            <h1 className="text-lg font-bold text-ink">Welcome to the team!</h1>
            <p className="mt-2 text-sm text-ink-muted">
              You've joined <strong>{info?.team_name}</strong>. Redirecting to pricing...
            </p>
          </>
        ) : info ? (
          <>
            <h1 className="text-lg font-bold text-ink">Join {info.team_name}</h1>
            <p className="mt-2 text-sm text-ink-muted">
              You've been invited to join a team on AAS Shield.
            </p>

            <div className="mt-4 rounded-lg bg-surface-50 p-4 text-left text-sm">
              <div className="flex justify-between">
                <span className="text-ink-muted">Team</span>
                <span className="font-semibold text-ink">{info.team_name}</span>
              </div>
              <div className="mt-2 flex justify-between">
                <span className="text-ink-muted">Members</span>
                <span className="font-semibold text-ink">{info.member_count}</span>
              </div>
              {info.member_count >= 2 && (
                <div className="mt-3 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
                  Team discount unlocked! Pay less for Fortress and Citadel plans.
                </div>
              )}
            </div>

            {error && (
              <p className="mt-3 text-sm text-red-600">{error}</p>
            )}

            {token ? (
              <button
                onClick={handleJoin}
                disabled={joining}
                className="mt-6 w-full rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark transition disabled:opacity-50"
              >
                {joining ? 'Joining...' : 'Join Team'}
              </button>
            ) : (
              <div className="mt-6">
                <p className="text-xs text-ink-muted mb-3">Sign in to join this team</p>
                <button
                  onClick={() => navigate(`/login?redirect=/join/${inviteToken}`)}
                  className="w-full rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark transition"
                >
                  Sign in to Join
                </button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
